import "./PopupCreateEvent/PopupCreateEvent.scss"

import { useState, useEffect } from "react"
import { useModalWindow } from "react-modal-global"

import { EventDto } from "@/entities/app/menu/menu.dto"
import { MenuAppInstance } from "@/entities/app/menu/menu.types"
import { Design } from "@/entities/design"
import PopupLayout from "@/services/Modal/layouts/PopupLayout/PopupLayout"
import Button from "@/shared/components/intrinsic/Button/Button"

import Step1EventBasics, { EventBasicsData } from "./PopupCreateEvent/Step1EventBasics"
import Step2DesignSelection from "./PopupCreateEvent/Step2DesignSelection"
import Step3ReviewConfirm from "./PopupCreateEvent/Step3ReviewConfirm"
import StepIndicator from "./PopupCreateEvent/StepIndicator"


interface PopupCreateEventProps {
  app: MenuAppInstance
  onSubmit?(event: EventDto): void | Promise<void>
  defaultValue?: EventDto
  defaultDesign?: Design | null
}

function PopupCreateEvent(props: PopupCreateEventProps) {
  const modal = useModalWindow()
  const [currentStep, setCurrentStep] = useState(1)
  const [pending, setPending] = useState(false)

  // Helper to extract date-only string from ISO datetime
  function extractDateOnly(isoString: string | undefined): string {
    if (!isoString) return ""
    // Extract YYYY-MM-DD from ISO string or already formatted date
    return isoString.split("T")[0]
  }

  // Helper to get selected days based on event data
  function getSelectedDays(defaultValue: EventDto | undefined): string[] {
    // If no default value (creating new), start with no days selected
    if (!defaultValue) {
      return []
    }

    // For specific_days mode, use the provided days if they exist
    if (defaultValue.specific_days && defaultValue.specific_days.length > 0) {
      return defaultValue.specific_days
    }

    // For other modes (daily, specific_dates), no days should be selected
    return []
  }

  // Helper to determine recurrence mode from event data
  function getRecurrenceMode(defaultValue: EventDto | undefined): "daily" | "specific_days" | "specific_dates" {
    if (!defaultValue) return "daily" // New events default to daily

    // Use the recurrence field directly if it exists
    if (defaultValue.recurrence) {
      return defaultValue.recurrence as "daily" | "specific_days" | "specific_dates"
    }

    // Fallback for legacy data
    return "daily"
  }

  // Initialize form data
  const [eventData, setEventData] = useState<EventBasicsData>({
    name: props.defaultValue?.name || "",
    description: props.defaultValue?.description || "",
    recurrenceMode: getRecurrenceMode(props.defaultValue),
    selectedDays: getSelectedDays(props.defaultValue),
    startDate: extractDateOnly(props.defaultValue?.specific_start_date),
    startTime: props.defaultValue?.start_time || "08:00",
    endDate: extractDateOnly(props.defaultValue?.specific_end_date),
    endTime: props.defaultValue?.end_time || "17:00"
  })

  const [selectedDesign, setSelectedDesign] = useState<Design | null>(props.defaultDesign ?? null)

  // Sync state when props change (handles modal reuse)
  useEffect(() => {
    setEventData({
      name: props.defaultValue?.name || "",
      description: props.defaultValue?.description || "",
      recurrenceMode: getRecurrenceMode(props.defaultValue),
      selectedDays: getSelectedDays(props.defaultValue),
      startDate: extractDateOnly(props.defaultValue?.specific_start_date),
      startTime: props.defaultValue?.start_time || "08:00",
      endDate: extractDateOnly(props.defaultValue?.specific_end_date),
      endTime: props.defaultValue?.end_time || "17:00"
    })
    setSelectedDesign(props.defaultDesign ?? null)
    setCurrentStep(1) // Reset to first step when opening
  }, [props.defaultValue, props.defaultDesign])

  function handleNext() {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  async function handleConfirm() {
    setPending(true)
    try {
      // Build the EventDto based on the recurrence mode
      const eventDto: EventDto = {
        name: eventData.name,
        description: eventData.description,
        recurrence: eventData.recurrenceMode,
        start_time: eventData.startTime,
        end_time: eventData.endTime,
        // Preserve existing designId if no new design selected
        designId: selectedDesign?.id ?? props.defaultValue?.designId
      }

      // Add mode-specific fields
      if (eventData.recurrenceMode === "specific_days") {
        // Specific days mode: add the selected days
        eventDto.specific_days = eventData.selectedDays
      } else if (eventData.recurrenceMode === "specific_dates") {
        // Specific dates mode: add the date range
        eventDto.specific_start_date = eventData.startDate
        eventDto.specific_end_date = eventData.endDate
      }
      // Daily mode: no additional fields needed

      await props.onSubmit?.(eventDto)
      modal.close()
    } catch (error) {
      console.error("Failed to create event:", error)
    } finally {
      setPending(false)
    }
  }

  function isStep1Valid(): boolean {
    const basicFieldsValid = eventData.name.trim().length > 0 &&
                             eventData.description.trim().length > 0 &&
                             eventData.startTime.trim().length > 0 &&
                             eventData.endTime.trim().length > 0

    // Daily mode: only needs basic fields (name, description, times)
    if (eventData.recurrenceMode === "daily") {
      return basicFieldsValid
    }

    // Specific days mode: needs basic fields + at least one day selected
    if (eventData.recurrenceMode === "specific_days") {
      return basicFieldsValid && eventData.selectedDays.length > 0
    }

    // Specific dates mode: needs basic fields + start date + end date
    if (eventData.recurrenceMode === "specific_dates") {
      return basicFieldsValid &&
             eventData.startDate.trim().length > 0 &&
             eventData.endDate.trim().length > 0
    }

    return false
  }

  function isStep2Valid(): boolean {
    // Design is optional, so always valid
    return true
  }

  function canProceedFromCurrentStep(): boolean {
    if (currentStep === 1) return isStep1Valid()
    if (currentStep === 2) return isStep2Valid()
    return true
  }

  return (
    <PopupLayout title="Scheduler Configuration" width="50em">
      <div className="popup-create-event">
        <StepIndicator currentStep={currentStep} />

        <div className="popup-create-event__content">
          {currentStep === 1 && (
            <Step1EventBasics data={eventData} onChange={setEventData} />
          )}
          {currentStep === 2 && (
            <Step2DesignSelection
              app={props.app}
              selectedDesign={selectedDesign}
              onChange={setSelectedDesign}
            />
          )}
          {currentStep === 3 && (
            <Step3ReviewConfirm data={eventData} selectedDesign={selectedDesign} companyId={props.app.companyId} />
          )}
        </div>

        <div className="popup-create-event__actions">
          {currentStep > 1 && (
            <Button color="dark" type="button" onClick={handleBack} disabled={pending}>
              Back
            </Button>
          )}

          <Button color="dark" type="button" onClick={modal.close} disabled={pending}>
            Cancel
          </Button>

          {currentStep < 3 && (
            <Button
              color="green"
              type="button"
              onClick={handleNext}
              disabled={!canProceedFromCurrentStep()}
            >
              Next
            </Button>
          )}

          {currentStep === 3 && (
            <Button
              color="green"
              type="button"
              onClick={handleConfirm}
              pending={pending}
              disabled={pending}
            >
              Confirm
            </Button>
          )}
        </div>
      </div>
    </PopupLayout>
  )
}

export default PopupCreateEvent
